import { useCallback, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { userDataAtom } from 'src/stores/auth/atoms';
import { closeModalAtom } from 'src/stores/modal/atoms';
import { getUpdatedUserInfoAtom } from 'src/stores/user/atom';
import { FeatureOptions } from 'src/types/AzarUser';
import { ModalType } from 'src/types/Modal';
import { OrderedModalHookType } from './types';
import withLoginUserModalPromise from './withLoginUserModalPromise';
const OSAFaceAgeVerificationModal = dynamic(() => import('src/components/OSAFaceAgeVerificationModal'), {
  ssr: false
});
const FaceTecAgeCheckFullModal = dynamic(() => import('src/components/AgeVerification/facetec'), {
  ssr: false
});
const YotiAgeCheckFullModal = dynamic(() => import('src/components/AgeVerification/yoti'), {
  ssr: false
});
type FaceAgeVerificationModal = React.ComponentType<{
  onComplete: () => void;
}>;
const AGE_VERIFICATION_METHOD_COMPONENT_MAP: Record<NonNullable<FeatureOptions['ageVerificationMethod']>, FaceAgeVerificationModal> = {
  OSA: OSAFaceAgeVerificationModal,
  FACE_CHECK: FaceTecAgeCheckFullModal,
  YOTI: YotiAgeCheckFullModal
};
const useFaceAgeVerificationModal: OrderedModalHookType = modalPropsPromise => {
  const userData = useAtomValue(userDataAtom);
  const isAgeVerificationRequired = userData?.featureOptions?.ageVerificationRequired;
  const ageVerificationMethod = userData?.featureOptions?.ageVerificationMethod;
  const setUserData = useSetAtom(userDataAtom);
  const getUpdatedUserInfo = useSetAtom(getUpdatedUserInfoAtom);
  const closeModal = useSetAtom(closeModalAtom);
  const onCompleteAgeVerification = useCallback(() => {
    closeModal(ModalType.FACE_AGE_VERIFICATION);

    // 인증 완료되면, ageVerificationRequired를 optimistic update와 함께 API 통해 갱신
    setUserData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        featureOptions: {
          ...prev.featureOptions,
          ageVerificationRequired: false
        }
      };
    });
    getUpdatedUserInfo();
  }, [closeModal, setUserData, getUpdatedUserInfo]);
  useEffect(() => {
    if (!modalPropsPromise) return;
    if (isAgeVerificationRequired) {
      const Component: FaceAgeVerificationModal = ageVerificationMethod ? AGE_VERIFICATION_METHOD_COMPONENT_MAP[ageVerificationMethod] : FaceTecAgeCheckFullModal;
      modalPropsPromise.resolve([{
        modalProps: {
          key: ModalType.FACE_AGE_VERIFICATION,
          component: () => <Component onComplete={onCompleteAgeVerification} />
        }
      }]);
    } else {
      modalPropsPromise.resolve(null);
    }
  }, [modalPropsPromise, isAgeVerificationRequired, ageVerificationMethod, onCompleteAgeVerification]);
};
export default withLoginUserModalPromise(useFaceAgeVerificationModal);